import { translateBackend } from '@assets/i18n/config';

import type { SxProps } from '@mui/material';
import { useAccordionTokens } from '@ovComponents/2-component/accordion/accordian.tokens';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { EmptyState } from '@ovComponents/2-component/emptyState/emptyState';

import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { HoldingItem } from '@ovComponents/4-module/holdingItem/holdingItem';
import { RefinitivSecurityModal } from '@ovComponents/4-module/widgets/refinitivSecurityModal/refinitivSecurityModal';
import {
  Box,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSupportColors } from '../holdings.resources';

export const AccordionHoldings = ({
  assetClassHoldings,
  loading,
  variant = 'elevated',
  showTotal,
  sx,
  options,
}: {
  assetClassHoldings: any[];
  loading: boolean;
  variant?: 'elevated' | 'outlined' | 'none';
  showTotal?: boolean;
  sx?: SxProps;
  options?: any;
}): JSX.Element => {
  const { t } = useTranslation('client');
  const theme = useTheme();
  const tokens = useAccordionTokens(theme);
  const supportColors = useSupportColors();

  const showAllocationPercentOnDecimalPlaces =
    options?.showAllocationTwoDecimalPlaces
      ? options?.showAllocationTwoDecimalPlaces
      : false;

  const [
    displayAssertClassDescriptionModal,
    setDisplayAssertClassDescriptionModal,
  ] = useState(false);
  const [modalHolding, setModalHolding] = useState<any>({});
  const [modalHoldingIndex, setModalHoldingIndex] = useState<number>(0);
  const [ricCode, setRicCode] = useState<string>('');
  const [holdingCurrency, setHoldingCurrency] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const showAsserClassDialog = (): void => {
    setDisplayAssertClassDescriptionModal(true);
  };

  const dialogClose = (): void => {
    setDisplayAssertClassDescriptionModal(false);
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Skeleton width='100%' height='300px' variant='rounded'></Skeleton>
        </Box>
      ) : assetClassHoldings.length === 0 ? (
        <></>
      ) : assetClassHoldings[0]?.totalCents === 0 ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <EmptyState>
            <Typography variant='bodyLarge'>
              {t('holdingsSummary.noHoldings')}
            </Typography>
          </EmptyState>
        </Box>
      ) : (
        <Box
          data-testid='accordion-holding'
          sx={{
            mt: 2,

            boxShadow:
              variant === 'elevated' ? theme.elevation.surface : 'none',

            borderRadius: theme.borderRadius.xl,

            border:
              variant === 'outlined'
                ? `1px solid ${theme.color.outlineVariant}`
                : 'none',
          }}
        >
          {assetClassHoldings
            .sort((a, b) => b.percentage - a.percentage)
            .map((item, index) => (
              <HoldingItem
                primaryHoldingIndex={index}
                testId={`${item.assetClass.key}-${index}`}
                key={`${item.assetClass.key}-${index}`}
                allPrimaryAssetClassHoldings={assetClassHoldings}
                onClick={(secondaryAsset: any) => {
                  setModalHolding(secondaryAsset);
                  showAsserClassDialog();
                  setModalHoldingIndex(index);
                }}
                primaryAssetClassHoldings={item}
                sx={sx}
                options={options}
              />
            ))}
          {showTotal && (
            <Box
              sx={{
                padding: '15px 12px',
                borderTop: `1px solid ${tokens.multipleBorderColor}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <Typography variant='titleMedium'>{t('total')}</Typography>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <AssetProgressBar
                  sx={{ width: '100%' }}
                  data={assetClassHoldings.map((item, index) => ({
                    key: `section-${item.assetClass.key}-${index}`,
                    percentage: item?.percentage ?? 0,
                    position: 1,
                    colorCode:
                      supportColors[index]?.active || theme.color.primary!,
                  }))}
                />

                <Typography
                  variant='bodySmall'
                  component='p'
                  sx={{ marginLeft: '10px' }}
                >
                  100%
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {displayAssertClassDescriptionModal ? (
        <Dialog
          open={displayAssertClassDescriptionModal}
          onClose={dialogClose}
          fullWidth
          maxWidth='sm'
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='headingMedium'>
                {translateBackend(modalHolding?.assetClass.translatedName)}
              </Typography>
              <IconButton aria-label='close' onClick={dialogClose}>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant='bodyMedium' sx={{ fontWeight: 500, mb: 3 }}>
              {translateBackend(modalHolding?.assetClass.translatedDescription)}
            </Typography>
            {(modalHolding?.securityHoldings ?? []).map(
              (item: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'start',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        height: 16,
                        width: 16,
                        mr: 1,

                        bgcolor:
                          supportColors[modalHoldingIndex].active ||
                          theme.color.primary!,

                        borderRadius: 1,
                      }}
                    />

                    <Typography
                      variant='bodyMedium'
                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => {
                        if (
                          item?.financialProduct?.ric &&
                          item.financialProduct.ric !== ''
                        ) {
                          setRicCode(item.financialProduct.ric);
                          setHoldingCurrency(item?.originalCurrency?.currency);
                          setModalOpen(true);
                        }
                      }}
                    >
                      {item.financialProduct.ticker ? (
                        <>
                          <b>{item.financialProduct.ticker}</b> -{' '}
                        </>
                      ) : (
                        ''
                      )}
                      {translateBackend(item.financialProduct.translatedName)}
                    </Typography>
                  </Box>
                  <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
                    {showAllocationPercentOnDecimalPlaces
                      ? `${(item?.percentage ?? 0).toFixed(2)}%`
                      : `${Math.round(item?.percentage ?? 0)}%`}
                  </Typography>
                </Box>
              ),
            )}
          </DialogContent>
        </Dialog>
      ) : undefined}
      {modalOpen && (
        <RefinitivSecurityModal
          onClose={() => {
            setModalOpen(false);
            setRicCode('');
            setHoldingCurrency(undefined);
          }}
          ricCode={ricCode}
          currency={holdingCurrency}
        />
      )}
    </>
  );
};
